<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { useDebounceFn } from "@vueuse/core";
  import { onMounted, nextTick, watch, watchEffect } from "vue";

  import IconHint from "./icon-hint.vue";
  import ServingSizeCategoryList from "./serving-size-category-list.vue";
  import ServingSizeBrowse from "./serving-size-browse.vue";
  import {
    allPossibleReferenceValues,
    amountFromUnit,
    computeConnectedFieldValue,
    computeServingSize,
    effectiveReferenceWeight,
    formatNumber,
    tagStyle,
    valueWithModifiers,
  } from "./serving-size-calculator.js";

  const GENERAL = "general";
  const INFANT = "infant";
  const FOOTNOTE_12 = `For raw fruit, vegetables, and fish, manufacturers should
follow the label statement for the serving size specified in Appendices
C and D to part 101
(<a href="https://www.ecfr.gov/current/title-21/part-101" target="_blank"
rel="noopener">21 CFR part 101</a>)
Code of Federal Regulations.`;
  const PIECE_HINT = `The term "piece" is used as a generic description of a
discrete unit. Use the description of a unit that is most appropriate for the
specific product (e.g., sandwich for sandwiches, cookie for cookies, etc).`;
  const VISUAL_UNIT_HINT = `Per 21 CFR 101.9(b)(5)(iii), ounce measurements should
include a visual unit of measure such as a dimension or description of a piece
(e.g., 1 oz (about \u00BD pickle), 2 oz (\u2153 cake)).`;

  const props = defineProps({
    perPackageWeight: { type: Number, default: null },
    displayUnit: { type: String, default: "g" },
    yieldMethod: { type: String, default: "package_size_and_count" },
    public: { type: Boolean, default: false },
    categoryId: { type: Number, default: null },
    visualUnitOfMeasure: { type: String, default: "" },
  });

  const emit = defineEmits(["fetch-error", "serving-change"]);

  let isLoading = _ref(false);

  let categoryInput = _ref(null);
  let searchedCategory = _ref(null);
  let browseGroup = _ref(null);
  let categories = _ref(null);
  let filteredCategories = _ref(null);
  let category = _ref(null);

  let type = _ref(GENERAL);

  let amount = _ref(null);
  let piece = _ref("piece");
  let visualUnit = _ref(props.visualUnitOfMeasure);
  let raccInput = _ref(null);

  let singleServingUnit = _ref("package");

  let selectedAmountIndex = _ref(0);
  let selectedLabelIndex = _ref(0);
  let selectedOptions = _ref({});

  const preselectCategory = () => {
    if (!props.categoryId || !categories.value) return;

    for (const section of [GENERAL, INFANT]) {
      const match = categories.value[section]?.categories?.find(({ id }) => id === props.categoryId);
      if (match) {
        type.value = section;
        select(match);
        return;
      }
    }
  };

  const fetchCategoriesIfNeeded = async () => {
    if (isLoading.value || categories.value) return;

    isLoading.value = true;
    try {
      const response = await fetch("/fda-serving-categories.json");
      const json = await response.json();
      categories.value = json;
      window.fdaServingCategories = json;
      preselectCategory();
    } catch (err) {
      window.Flash.error("Error fetching FDA categories");
      emit("fetch-error");
    } finally {
      isLoading.value = false;
      nextTick(() => categoryInput.value?.focus());
    }
  };

  const searchLabel = _computed(() => {
    if (browseGroup.value) return `Search in ${browseGroup.value}`;
    return "Search product category";
  });

  const resultsLabel = _computed(() => {
    if (browseGroup.value) return `Results from ${browseGroup.value}`;
    return "Results from all categories";
  });

  const search = (query) => {
    if (!categories.value || query.length < 3) return;

    const terms = query.trim().toLowerCase().split(/\s+/);
    const pool = browseGroup.value
      ? categories.value[type.value].categories.filter(({ group }) => group === browseGroup.value)
      : categories.value[type.value].categories;

    filteredCategories.value = pool.filter(({ name, group }) => {
      const text = `${name} ${group}`.toLowerCase();
      return terms.every((term) => text.includes(term));
    });
  };

  const searchWithDebounce = useDebounceFn(search, 300);

  const onInput = (event) => {
    searchWithDebounce(event.target.value);
  };

  const see = (term) => {
    searchedCategory.value = term;
    search(term);
  };

  const searchAllCategories = () => {
    browseGroup.value = null;
    search(searchedCategory.value);
  };

  const searchInGroup = (group) => {
    browseGroup.value = group;
    search(searchedCategory.value);
  };

  const isPieceBased = _computed(() => {
    if (typeof selectedLabelUnit.value === "string" && selectedLabelUnit.value?.includes("piece")) return true;

    if (customPieceName.value) return true;

    if (
      typeof selectedLabelUnit.value === "object" &&
      selectedLabelUnit.value &&
      selectedLabelUnit.value.label.includes("piece")
    )
      return true;

    return false;
  });

  const select = (selectedCategory) => {
    category.value = selectedCategory;
    amount.value = null;
    selectedAmountIndex.value = 0;
    selectedLabelIndex.value = 0;
  };

  const reset = (focus = false) => {
    category.value = null;
    isLoading.value = false;
    selectedAmountIndex.value = 0;
    selectedLabelIndex.value = 0;
    selectedOptions.value = {};
    filteredCategories.value = null;
    searchedCategory.value = null;
    browseGroup.value = null;
    piece.value = "piece";
    visualUnit.value = props.visualUnitOfMeasure;
    raccInput.value = null;
    singleServingUnit.value = "package";
    nextTick(() => {
      if (categoryInput.value) {
        if (focus) categoryInput.value.focus();
      }
    });
  };

  const typeCategories = _computed(() => categories.value?.[type.value]?.categories || []);

  const isSearching = _computed(() => searchedCategory.value?.length >= 3);

  const selectedAmount = _computed(() => {
    if (!category.value?.amounts?.length) return null;

    return category.value.amounts[selectedAmountIndex.value];
  });

  const selectedModifiers = _computed(() => {
    if (!selectedAmount.value) return null;

    const options = category.value.options;

    let modifiers = [];
    (selectedAmount.value.options ?? []).forEach((optionName) => {
      const option = options?.find(({ name }) => name === optionName);
      if (!option) return;

      if (option.type === "checkbox") {
        if (!isSelected(optionName, true)) return;

        modifiers = [...modifiers, ...option.modifiers];
      } else if (option.type === "radio") {
        const selectedValue = option.values.find(({ label }) => isSelected(optionName, label));
        if (!selectedValue) return;

        modifiers = [...modifiers, ...selectedValue.modifiers];
      }
    });

    return modifiers;
  });

  const selectedReferenceAmount = _computed(() => {
    if (!selectedAmount.value?.value) return null;

    return valueWithModifiers(selectedAmount.value.value, selectedModifiers.value);
  });

  const availableOptions = _computed(() => {
    return category.value?.options?.filter(({ name }) => selectedAmount.value?.options?.includes(name));
  });

  const labelUnits = _computed(() => {
    if (!selectedAmount.value?.fixed_labels && !selectedAmount.value?.label_units) return null;

    const fixedOverride = selectedModifiers.value?.find(
      ({ type }) => type === "fixed_labels"
    )?.fixed_labels;
    return fixedOverride || selectedAmount.value.fixed_labels || selectedAmount.value.label_units;
  });

  const selectedLabelUnit = _computed(() => {
    if (!labelUnits.value?.length) return null;

    return labelUnits.value[selectedLabelIndex.value] || labelUnits.value[0];
  });

  const hasVisualUnit = _computed(() => !!selectedLabelUnit.value?.visual_unit);
  const isOzVisual = _computed(() => !!selectedAmount.value?.oz_visual);
  const hasRaccInput = _computed(
    () => !!selectedLabelUnit.value?.racc_fraction || !!selectedLabelUnit.value?.racc_input
  );
  const raccInputLabel = _computed(
    () => selectedLabelUnit.value?.racc_input || `Weight per ${selectedLabelUnit.value?.racc_unit} (g)`
  );

  const effectiveAmount = _computed(() => {
    if (!hasRaccInput.value || !selectedAmount.value) return selectedAmount.value;

    const fraction = selectedLabelUnit.value.racc_fraction || 1;
    const raccWeight = raccInput.value ? Number(raccInput.value) * fraction : null;
    return { ...selectedAmount.value, value: raccWeight };
  });

  const inputType = _computed(() => selectedAmount.value?.input_type || category.value?.input_type);

  const servingSize = _computed(() => {
    return computeServingSize({
      referenceAmount: effectiveAmount.value,
      amount: amount.value,
      inputType: inputType.value,
      labelUnit: selectedLabelUnit.value,
      pieceUnit: pieceUnit.value,
      showPieceUnit: showPieceUnit.value,
      visualUnit: visualUnit.value,
      modifiers: selectedModifiers.value,
      perPackageWeight: props.perPackageWeight,
      singleServingUnit: singleServingUnit.value,
      category: category.value,
    });
  });

  const servingDeclaration = _computed(() => {
    if (servingSize.value.visualUnit == null) return servingSize.value.serving;

    const weight = `${formatNumber(servingSize.value.servingWeight, false)}${props.displayUnit}`;
    const visual = servingSize.value.visualUnit || "visual unit of measure";
    return `${servingSize.value.serving} (${weight}/${visual})`;
  });

  const labelStatement = _computed(() => {
    const statement = category.value.label_statement;
    const visual = servingSize.value?.visualUnit || visualUnit.value;

    return visual ? statement.replaceAll("visual unit of measure", visual) : statement;
  });

  const inputLabel = _computed(() => {
    if (selectedAmount.value?.input || category.value?.input) {
      return selectedAmount.value?.input || category.value?.input;
    }

    if (selectedAmount.value?.ruleset === 3 && selectedLabelUnit.value) {
      const unit = selectedLabelUnit.value.unit || selectedLabelUnit.value;
      return `Weight per ${unit} (g)`;
    }

    if (isPieceBased.value) {
      if (selectedAmount.value?.ruleset === 2) {
        return `Weight of 1 whole ${pieceName.value} (g)`;
      }
      const startsWithVowel = ["a", "e", "i", "o", "u"].reduce(
        (res, letter) => res || pieceName.value.startsWith(letter),
        false
      );
      const connector = startsWithVowel ? "an" : "a";
      return `Weight of ${connector} ${pieceName.value} (g)`;
    }

    return "Your serving weight/volume (g/mL)";
  });

  const amountToMake = _computed(() =>
    amountFromUnit(amount.value, selectedLabelUnit.value?.unit || selectedLabelUnit.value)
  );

  const hintContent = (text) => {
    const match = text.match(/##footnote-(\d+)/);

    if (match) {
      const footnoteNumber = Number(match[1]);
      if (footnoteNumber === 12) return FOOTNOTE_12;

      return categories.value[type.value].footnotes[footnoteNumber - 1];
    } else {
      return text;
    }
  };

  const connectedFieldValue = _computed(() => {
    return computeConnectedFieldValue({
      servingSize: servingSize.value,
      inputType: inputType.value,
      amountToMake: amountToMake.value,
      referenceAmount: selectedReferenceAmount.value,
      perPackageWeight: props.perPackageWeight,
      yieldMethod: props.yieldMethod,
    });
  });

  const servingsCount = _computed(() => {
    if (props.yieldMethod === "ingredient_weight") {
      const weight = servingSize.value?.servingWeight;
      if (!weight || !props.perPackageWeight) return;

      return props.perPackageWeight / weight;
    } else if (props.yieldMethod === "package_size_and_count") {
      return connectedFieldValue.value;
    }
  });

  const yourReferenceAmount = _computed(() => {
    const raccAmount = hasRaccInput.value ? raccInput.value : amount.value;
    const value =
      selectedReferenceAmount.value ||
      effectiveReferenceWeight({
        labelUnit: selectedLabelUnit.value,
        amount: raccAmount,
      });

    if (!value) return;

    return `${formatNumber(value, false)} ${selectedAmount.value.unit}`;
  });

  const possibleReferenceValues = _computed(() => allPossibleReferenceValues(category.value));

  const showYourReferenceAmount = _computed(() => {
    if (!yourReferenceAmount.value) return false;

    const distinct = new Set(possibleReferenceValues.value.map(({ value, unit }) => `${value}${unit}`));
    return distinct.size > 1;
  });

  const alwaysSingleServing = _computed(() => {
    if (!props.perPackageWeight) return false;

    const minValue = Math.min(...possibleReferenceValues.value.map(({ value }) => value));
    return props.perPackageWeight < 2 * minValue;
  });

  const showPieceUnit = _computed(() => {
    let show = false;
    if (isPieceBased.value) {
      show = true;
    } else if (inputType.value === "compound_format") {
      const { secondary } = selectedAmount.value.compound_units;
      show = secondary.includes("piece");
    }

    return show;
  });

  const pieceUnit = _computed(() => {
    let u;
    if (isPieceBased.value) {
      u = piece.value;
    } else if (inputType.value === "compound_format") {
      const { secondary } = selectedAmount.value.compound_units;
      u = secondary.includes("piece") ? piece.value : secondary;
    }
    return customPieceName.value || u || "piece";
  });

  const customPieceName = _computed(() => selectedAmount.value?.piece || category.value?.piece);

  const pieceName = _computed(() => customPieceName.value || piece.value || "piece");

  const hasVariableReference = _computed(
    () => selectedLabelUnit.value?.amount && !selectedLabelUnit.value?.visual_unit
  );

  const showAmountInput = _computed(() => {
    if (footnoteCategory.value) return false;

    if (servingSize.value?.singleServing && !hasVariableReference.value) return false;

    if (!selectedAmount.value?.fixed_labels) return true;

    return hasVariableReference.value;
  });

  const toggleOption = (name, value) => {
    if (value) {
      const newValue = isSelected(name, value) ? null : value;
      selectOption(name, newValue);
    } else {
      selectOption(name, !selectedOptions.value[name]);
    }
  };

  const selectOption = (name, value) => {
    selectedOptions.value = {
      ...selectedOptions.value,
      [name]: value,
    };
  };

  const isSelected = (name, value) => selectedOptions.value[name] == value;

  const footnoteCategory = _computed(() => category.value?.label_statement === "See footnote");

  watch(
    () => type.value,
    () => {
      browseGroup.value = null;
      if (searchedCategory.value) search(searchedCategory.value);
    }
  );

  watch(
    () => selectedAmount.value,
    () => {
      if (customPieceName.value) piece.value = customPieceName.value;
      else if (selectedAmount.value?.ruleset === 2) piece.value = "unit";
      else piece.value = "piece";
    }
  );

  onMounted(() => {
    if (window.fdaServingCategories) {
      categories.value = window.fdaServingCategories;
      preselectCategory();
    } else {
      fetchCategoriesIfNeeded();
    }
  });

  watchEffect(() => {
    emit("serving-change", { servingSize: servingSize.value, connectedFieldValue: connectedFieldValue.value, categoryId: category.value?.id });
  });
</script>

<template>
  <div :class="{ public: props.public }">
    <template v-if="isLoading">
      <div class="is-flex is-justify-content-center">
        <div class="icon is-large spinning-element fast-spin">
          <i class="fa fa-3x fa-circle-notch"></i>
        </div>
      </div>
    </template>
    <template v-else-if="!category">
      <div :class="{ 'is-centered': !props.public }" class="buttons has-addons mb-3">
        <button class="button" :class="{ 'is-primary': type === GENERAL }" @click="type = GENERAL">
          General foods
        </button>
        <button class="button" :class="{ 'is-primary': type === INFANT }" @click="type = INFANT">
          Infants and young (1-3 years of age)
        </button>
      </div>
      <div class="field">
        <label class="label" for="category">{{ searchLabel }}</label>
        <div class="control has-icons-right">
          <input
            id="category"
            ref="categoryInput"
            v-model="searchedCategory"
            type="text"
            class="input"
            placeholder="e.g. Bread, syrup, candies, bar"
            autocomplete="off"
            @input="onInput"
          />
          <span
            v-if="searchedCategory"
            class="icon is-small is-right is-clickable"
            @click="reset(true)"
          >
            <i class="fas fa-times"></i>
          </span>
        </div>
      </div>
      <div class="mt-3">
        <template v-if="isSearching">
          <p class="heading mb-3">
            {{ resultsLabel }}
            <a v-if="browseGroup" class="ml-2 is-size-7" @click="searchAllCategories">
              Search all categories
            </a>
          </p>
          <div v-if="!filteredCategories?.length" class="well">No categories found</div>
          <ServingSizeCategoryList
            v-else
            :categories="filteredCategories"
            :clickable-groups="!browseGroup"
            @select="select"
            @see="see"
            @group-click="searchInGroup"
          />
        </template>
        <ServingSizeBrowse
          v-else-if="typeCategories.length"
          v-model="browseGroup"
          :categories="typeCategories"
          @select="select"
          @see="see"
        />
      </div>
    </template>
    <template v-else-if="category">
      <div id="selected">
        <div class="is-flex">
          <div>
            <dl>
              <dt class="has-flex-vcentered gap-3">
                <span>Category</span>
                <button class="button is-small" @click="reset(true)">Change</button>
              </dt>
              <dd>
                {{ category.name }}
                <IconHint
                  v-if="category.hint"
                  :content="hintContent(category.hint)"
                  :interactive="true"
                />
              </dd>
            </dl>
            <div class="mt-1">
              <div v-if="category.group" class="tag" :style="tagStyle(category.group)">
                {{ category.group }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="serving" class="mt-3">
        <div v-if="!alwaysSingleServing" class="is-flex is-justify-content-space-between mb-2">
          <div v-if="category.amounts.length > 1" class="buttons has-addons is-centered mb-0">
            <button
              v-for="(amt, index) in category.amounts"
              :key="index"
              class="button"
              :class="{ 'is-primary': selectedAmountIndex === index }"
              @click="selectedAmountIndex = index"
            >
              <span>{{ amt.name }}</span>
              <IconHint
                v-if="amt.hint"
                :content="hintContent(amt.hint)"
                :interactive="true"
                :hint-classes="null"
              />
            </button>
          </div>

          <div v-if="labelUnits?.length > 1" class="buttons has-addons is-centered mb-0">
            <button
              v-for="(unt, index) in labelUnits"
              :key="index"
              class="button"
              :class="{ 'is-primary': selectedLabelIndex === index }"
              @click="selectedLabelIndex = index"
            >
              <span>{{ unt.label || unt }}</span>
            </button>
          </div>
        </div>

        <div
          v-for="option in alwaysSingleServing ? [] : availableOptions"
          :key="option.name"
          class="mb-2"
        >
          <label v-if="option.type === 'checkbox'" class="checkbox">
            <input
              type="checkbox"
              :value="option.value"
              :checked="isSelected(option.name, true)"
              @change="toggleOption(option.name)"
            />
            {{ option.label }}
          </label>
          <div v-if="option.type === 'radio'" class="is-flex gap-2">
            <label v-for="val in option.values" :key="val" class="is-inline-flex gap-1">
              <input
                type="radio"
                :checked="isSelected(option.name, val.label)"
                @click="toggleOption(option.name, val.label)"
              />
              <span>{{ val.label }}</span>
              <IconHint v-if="val.hint" :content="hintContent(val.hint)" :interactive="true" />
            </label>
          </div>
        </div>

        <div class="input-group">
          <div v-if="hasRaccInput" class="field mb-2">
            <label for="racc-input" class="label">{{ raccInputLabel }}</label>
            <div class="control">
              <input id="racc-input" v-model="raccInput" type="text" class="input" />
            </div>
          </div>
          <div v-if="showAmountInput">
            <div class="field mb-2">
              <label for="amount" class="label">{{ inputLabel }}</label>
              <div class="control">
                <input
                  id="amount"
                  v-model="amount"
                  :class="{ 'is-warning': servingSize?.warnings?.length }"
                  type="text"
                  class="input"
                />
              </div>
            </div>
            <div v-if="servingSize?.warnings?.length" class="mb-2">
              <p
                v-for="warning in servingSize?.warnings"
                :key="warning"
                class="has-text-warning-dark"
              >
                {{ warning }}
              </p>
            </div>
          </div>

          <div
            v-if="hasVisualUnit && !servingSize?.singleServing"
            class="field mb-2 visual-unit-input"
          >
            <label for="visual-unit" class="label">
              <span class="icon-text">
                <span>Visual unit of measure</span>
                <IconHint :content="VISUAL_UNIT_HINT" :interactive="true" />
              </span>
            </label>
            <div class="control">
              <input
                id="visual-unit"
                v-model="visualUnit"
                type="text"
                class="input"
                placeholder="e.g. ⅓ cake, about ½ pickle, 1 inch slice"
              />
            </div>
          </div>

          <div
            v-if="showPieceUnit && !customPieceName && !servingSize?.singleServing"
            class="field mb-2"
          >
            <label for="piece" class="label">
              <span class="icon-text">
                <span>{{
                  isOzVisual ? "Visual unit of measure" : "Piece (cookie, bar, etc.)"
                }}</span>
                <IconHint
                  :content="isOzVisual ? VISUAL_UNIT_HINT : PIECE_HINT"
                  :interactive="true"
                />
              </span>
            </label>
            <div class="control">
              <input
                id="piece"
                v-model="piece"
                type="text"
                class="input"
                :placeholder="isOzVisual ? 'e.g. fillet, cake, patty' : ''"
              />
            </div>
          </div>

          <div v-if="servingSize?.singleServing" class="field mb-2">
            <label for="single-serving-unit" class="label"
              >Unit name (package, bottle, can, etc.)</label
            >
            <div class="control">
              <input
                id="single-serving-unit"
                v-model="singleServingUnit"
                type="text"
                class="input"
                placeholder="e.g. bottle, can, pouch, bar"
              />
            </div>
          </div>
        </div>

        <dl v-if="servingSize" class="serving-size">
          <dt>Suggested serving size</dt>
          <dd class="is-inline-flex gap-1 is-align-items-center">
            <span>{{ servingDeclaration }}</span>
            <IconHint
              v-if="servingSize.hint"
              :content="hintContent(servingSize.hint)"
              :interactive="true"
            />
          </dd>
          <p v-if="servingSize.singleServing" class="is-size-7 mt-1">
            This is a single-serving package (&lt; 200% RACC)
          </p>
          <div class="mt-2 is-flex is-justify-content-space-around">
            <dl v-if="props.perPackageWeight">
              <dt class="is-size-6 mt-2">Per package weight</dt>
              <dd class="is-size-6">
                {{ formatNumber(props.perPackageWeight, false) }} {{ props.displayUnit }}
              </dd>
            </dl>
            <dl v-if="servingsCount" :class="{ 'has-text-danger': servingsCount < 1 }">
              <dt class="is-size-6 mt-2">Servings</dt>
              <dd class="is-size-6 gap-1 is-inline-flex is-align-items-center">
                <span>{{ formatNumber(servingsCount, false) }}</span>
                <IconHint
                  v-if="servingsCount < 1"
                  content="Recipes should have at least one serving"
                  hint-classes="has-text-danger"
                />
              </dd>
            </dl>
            <dl v-if="servingSize?.servingWeight">
              <dt class="is-size-6 mt-2">Serving size weight</dt>
              <dd class="is-size-6">
                {{ formatNumber(servingSize.servingWeight, false) }} {{ props.displayUnit }}
              </dd>
            </dl>
          </div>
        </dl>
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="footnoteCategory"
          class="serving-size is-size-6"
          v-html="hintContent(category.text)"
        ></div>
        <!-- eslint-enable vue/no-v-html -->

        <div class="extra-info is-flex mt-4 gap-3">
          <div class="box is-shadowless">
            <dl class="mb-3">
              <dt>FDA Reference amount(s)</dt>
              <dd>{{ category.reference_amount }}</dd>
            </dl>
            <dl v-if="showYourReferenceAmount" class="mb-3">
              <dt>Your reference amount</dt>
              <dd>{{ category.reference_amount }}</dd>
            </dl>
            <dl>
              <dt>Example(s)</dt>
              <dd>
                {{ labelStatement }}
                <IconHint v-if="footnoteCategory" :content="FOOTNOTE_12" :interactive="true" />
              </dd>
            </dl>
          </div>

          <div v-if="servingSize?.raccUnits" class="box is-shadowless">
            <dl v-if="servingSize?.raccPercentage" class="mb-2">
              <dt>Percentage of RACC</dt>
              <dd>{{ servingSize.raccPercentage }}%</dd>
            </dl>
            <dl v-if="servingSize?.raccUnits">
              <dt>Units per RACC</dt>
              <dd>
                {{ formatNumber(servingSize.raccUnits, false) }}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  @import "~/jose_modern/css";

  .public {
    .input-group {
      display: flex;
      gap: 2em;
      margin-bottom: 1.5em;
      flex-wrap: wrap;
    }
  }

  #selected,
  #serving {
    dl {
      margin: 0;
    }
    dt {
      font-weight: bold;
    }
  }

  #serving {
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1em;

      & > *:nth-child(3) {
        grid-column: 1 / -1;
      }
    }

    .serving-size {
      font-size: 1.3em;
      padding: 1.75em;
      border-radius: 0.5em;
      text-align: center;
      color: darken($primary, 15%);
      background: rgba($primary, 0.1);
    }
  }

  .extra-info {
    .box {
      margin-bottom: 0;
      flex-grow: 1;
    }

    dt {
      font-size: 0.5em;
      font-family: $family-secondary;
      text-transform: uppercase;
      color: $dark;
      margin-bottom: 1em;
      letter-spacing: 1.5px;
    }
  }
</style>
